import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Clock, Zap, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = d.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60000);
  const hrs = Math.round(mins / 60);
  const days = Math.round(hrs / 24);
  const label = days >= 1 ? `${days}d` : hrs >= 1 ? `${hrs}h` : `${mins}m`;
  return diffMs > 0 ? `in ${label}` : `${label} ago`;
};

export const ScheduleCard = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/schedule").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // refresh every 60s
    return () => clearInterval(id);
  }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/schedule/run-now");
      if (r.data?.error) toast.error(`Refresh failed: ${r.data.error}`);
      else toast.success(`Refresh complete · ${r.data.refresh_total_games ?? 0} games · ${r.data.bets_settled ?? 0} bets settled`);
      await load();
    } catch (e) {
      toast.error("Run failed");
    } finally { setBusy(false); }
  };

  if (!s) return null;

  const hasError = !!s.last_error;
  const ok = !!s.last_result && !hasError;
  const StatusIcon = hasError ? AlertTriangle : ok ? CheckCircle2 : Clock;
  const statusColor = hasError ? "text-red" : ok ? "text-green-dark" : "text-secondary";

  return (
    <div className="iso-card p-4 flex flex-wrap items-center gap-4" data-testid="schedule-card">
      <div className={`flex size-10 items-center justify-center rounded-xl bg-secondary/10 ${statusColor}`}>
        <StatusIcon className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-gray-900">Daily auto-refresh</div>
          <span className="iso-chip iso-chip-secondary !text-[10px]">{s.configured_at_utc} UTC</span>
        </div>
        <div className="mt-0.5 text-xs text-gray-500">
          Next run <span className="font-semibold text-gray-900">{fmtRelative(s.next_run_at)}</span>
          {s.last_run_at && (
            <> · Last run <span className="font-semibold text-gray-900">{fmtRelative(s.last_run_at)}</span></>
          )}
          {s.last_result && (
            <> · {s.last_result.refresh_total_games ?? 0} games synced, {s.last_result.bets_settled ?? 0} bets settled</>
          )}
          {hasError && <span className="ml-1 text-red-dark">· {s.last_error}</span>}
        </div>
      </div>
      <button
        onClick={runNow}
        disabled={busy}
        className="iso-btn-outline !py-1.5 !text-xs"
        data-testid="schedule-run-now"
      >
        {busy ? <Zap className="size-3.5 animate-pulse" /> : <Zap className="size-3.5" />}
        Run now
      </button>
    </div>
  );
};
